import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { FormField } from "components/form/FormField/FormField";
import { RichEditor } from "components/RichEditor/RichEditor";
import { Button } from "components/ui/Button";
import { Dialog } from "components/ui/Dialog";
import { Select } from "components/ui/Select/Select";
import { useCreatePatientLetter } from "features/communications/hooks/useCreatePatientLetter";
import { useLetterTemplates } from "features/templates/hooks/useLetterTemplates";

import styles from "./AddLetterModal.module.css";

const LETTER_TYPE_OPTIONS = [
  { value: "General", label: "General" },
  { value: "Referral", label: "Referral" },
  { value: "Discharge", label: "Discharge" },
  { value: "Other", label: "Other" },
];

const formSchema = z.object({
  name: z.string().min(1, { message: "Please provide a letter title." }),
  letterType: z.string().min(1, { message: "Please select a type." }),
  text: z.string().optional(),
});

export const AddLetterModal = ({ open, patientId, onClose, onSuccess }) => {
  const { data: templates = [] } = useLetterTemplates(patientId);
  const { mutateAsync: createLetter } = useCreatePatientLetter();

  const [text, setText] = useState("");
  const [editorKey, setEditorKey] = useState(0);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      letterType: "",
      text: "",
    },
  });

  const templateOptions = [
    { value: "", label: "Blank letter" },
    ...templates.map((template) => ({ value: String(template.id), label: template.name })),
  ];

  const handleTemplateSelect = (value) => {
    setSelectedTemplateId(value);

    if (!value) return;

    const template = templates.find((t) => String(t.id) === value);

    if (!template) return;

    if (!form.getValues("name")) {
      form.setValue("name", template.name);
    }

    setText(template.template ?? "");
    form.setValue("text", template.template ?? "");
    setEditorKey((previous) => previous + 1);
  };

  const handleSave = async () => {
    const isValid = await form.trigger();

    if (!isValid) return;

    setSaveError(null);
    setIsSaving(true);

    try {
      const values = form.getValues();
      await createLetter({
        patientId,
        name: values.name,
        letterType: values.letterType,
        text,
        status: "DRAFT",
      });
      onSuccess();
    } catch {
      setSaveError("Failed to save letter. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    form.reset();
    setText("");
    setEditorKey((previous) => previous + 1);
    setSelectedTemplateId("");
    setSaveError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} title="New Letter" width={860} minHeight={600}>
      <FormProvider {...form}>
        <div className={styles.content}>
          <div className={styles.formRow}>
            <FormField name="name" label="Letter title *">
              <input
                className={styles.textInput}
                placeholder="Enter letter title"
                {...form.register("name")}
              />
            </FormField>

            <FormField name="letterType" label="Type *">
              <Select
                options={LETTER_TYPE_OPTIONS}
                value={form.watch("letterType")}
                onChange={(value) => form.setValue("letterType", value)}
                fullWidth
              />
            </FormField>
          </div>

          <div className={styles.templateRow}>
            <label className={styles.templateLabel}>Template</label>
            <Select
              options={templateOptions}
              value={selectedTemplateId}
              onChange={handleTemplateSelect}
              fullWidth
            />
          </div>

          <FormField name="text" label="Content">
            <RichEditor
              key={editorKey}
              editorValue={text}
              onChange={(value) => {
                setText(value);
                form.setValue("text", value);
              }}
            />
          </FormField>

          <div className={styles.footer}>
            <span className={styles.footerHint}>PDF generated on save</span>

            <div className={styles.footerActions}>
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>

              <Button variant="primary" onClick={handleSave} disabled={isSaving}>
                {isSaving ? "Saving..." : "Save Letter"}
              </Button>
            </div>
          </div>

          {saveError && <div className={styles.errorText}>{saveError}</div>}
        </div>
      </FormProvider>
    </Dialog>
  );
};
